import { FetchBaseQueryMeta } from '@reduxjs/toolkit/query'

import {
  getEnrichedSortParam,
  transformBodyByHttpMethod,
  transformResponseWithPagination,
} from '@helpers'
import { TYPES_SOURCE_OPTIONS, URLS } from '@constants'
import { CommandResponse, CommandTypeDTO, ExecuteCommandPayload, ObjectDataRecord } from '@types'

import { commonAPI } from './common.api'
import { responseFileHandler } from './config'

export const commandAPI = commonAPI.injectEndpoints({
  endpoints: build => ({
    executeCommand: build.mutation<any, ExecuteCommandPayload>({
      query: ({
        commandCode,
        objectCode,
        page,
        size,
        filter,
        expands,
        params,
        body,
        method = 'POST',
        resource,
      }) => {
        const { sort, ...restParams } = params || {}

        return {
          url: `/command/${
            resource === TYPES_SOURCE_OPTIONS.SYSTEM ? 'systems' : 'objects'
          }/${objectCode}/${commandCode}?${getEnrichedSortParam((sort || []) as string[])}`,
          method,
          params: {
            page,
            size,
            filter,
            expands,
            ...restParams,
          },
          body: transformBodyByHttpMethod(method, body),
          responseHandler: responseFileHandler,
        }
      },
      transformResponse: (
        output: ObjectDataRecord[] | CommandResponse[] | CommandResponse,
        meta: FetchBaseQueryMeta,
        arg
      ) => {
        if (arg?.isCommandResponse) {
          const outputArray = Array.isArray(output) ? output : [output]

          const result = outputArray.map(response => {
            const { object, ...commandData } = response as CommandResponse

            return {
              ...object,
              command_response_data: commandData,
            }
          })

          return result
        }

        if (arg?.transformResponseWithPagination) {
          return transformResponseWithPagination<ObjectDataRecord[]>(
            output as ObjectDataRecord[],
            meta
          )
        }

        return output
      },
    }),
    executeCommandWithCache: build.query<any, ExecuteCommandPayload>({
      query: ({
        commandCode,
        objectCode,
        page,
        size,
        filter,
        expands,
        sort,
        params,
        body,
        method = 'POST',
      }) => ({
        url: `/command/objects/${objectCode}/${commandCode}`,
        method,
        params: {
          page,
          size,
          filter,
          sort,
          expands,
          ...params,
        },
        body: transformBodyByHttpMethod(method, body),
      }),
      transformResponse: (output: unknown[], meta: FetchBaseQueryMeta, arg) => {
        if (arg.transformResponseWithPagination) {
          return transformResponseWithPagination<unknown[]>(output, meta)
        }

        return output
      },
    }),
    fetchCommandsByObjectCode: build.query<CommandTypeDTO[], string | void>({
      query: objectCode => ({
        url: URLS.CORE_API + `/models/object/code/${objectCode}/commands`,
      }),
    }),
    fetchCommandParameters: build.query<
      CommandTypeDTO,
      { objectCode: string; commandCode: string; typeSource: string }
    >({
      query: ({ objectCode, commandCode, typeSource }) => ({
        url:
          URLS.CORE_API + `/models/${typeSource}/code/${objectCode}/commands/code/${commandCode}`,
      }),
    }),
    fetchCommandsBySystemCode: build.query<CommandTypeDTO[], string | void>({
      query: systemCode => ({
        url: URLS.CORE_API + `/models/system/code/${systemCode}/commands`,
      }),
    }),
  }),
})

export const {
  useExecuteCommandMutation,
  useLazyExecuteCommandWithCacheQuery,
  useFetchCommandsByObjectCodeQuery,
  useFetchCommandParametersQuery,
  useFetchCommandsBySystemCodeQuery,
} = commandAPI
